/* Social.jsx - Public KVKK consent + social media subscribe page (multilingual) */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Youtube, Instagram, Music2, Facebook, Check, FileText, Globe, Loader2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TXT = {
    tr: {
        title: 'Sosyal Medya',
        subtitle: 'Bizi takip edin',
        subscribe: 'Abone Ol',
        kvkkButton: 'KVKK Rıza Metnini Oku',
        kvkkTitle: 'KVKK Rıza Metni',
        nameLabel: 'Ad Soyad',
        namePh: 'Adınız ve soyadınız',
        roomLabel: 'Oda No',
        roomPh: 'Örn: 215',
        agreeButton: 'Okudum, Onaylıyorum',
        agreed: 'Rıza onayınız kaydedildi. Teşekkürler!',
        formNote: 'Video çekimi onayı için bilgilerinizi girin ve okuduktan sonra onaylayın.',
        fillAll: 'Lütfen Ad-Soyad ve Oda No alanlarını doldurun',
    },
    en: {
        title: 'Social Media',
        subtitle: 'Follow us',
        subscribe: 'Subscribe',
        kvkkButton: 'Read Consent Form',
        kvkkTitle: 'Consent Form',
        nameLabel: 'Full Name',
        namePh: 'Your full name',
        roomLabel: 'Room No',
        roomPh: 'e.g. 215',
        agreeButton: 'I Read and Consent',
        agreed: 'Your consent has been recorded. Thank you!',
        formNote: 'Enter your details and confirm after reading to consent to video recording.',
        fillAll: 'Please fill in Full Name and Room No',
    },
    ru: {
        title: 'Соцсети',
        subtitle: 'Подпишитесь',
        subscribe: 'Подписаться',
        kvkkButton: 'Прочитать согласие',
        kvkkTitle: 'Форма согласия',
        nameLabel: 'Имя Фамилия',
        namePh: 'Ваше имя и фамилия',
        roomLabel: 'Номер комнаты',
        roomPh: 'напр. 215',
        agreeButton: 'Прочитал(а) и согласен',
        agreed: 'Ваше согласие записано. Спасибо!',
        formNote: 'Введите данные и подтвердите после прочтения, чтобы дать согласие на видеосъёмку.',
        fillAll: 'Заполните Имя Фамилия и Номер комнаты',
    },
};

const LANGS = [
    { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'ru', label: 'Русский', flag: '🇷🇺' },
];

export default function Social() {
    const [lang, setLang] = useState(null);  // null = show language picker first
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [kvkkOpen, setKvkkOpen] = useState(false);
    const [fullName, setFullName] = useState('');
    const [roomNumber, setRoomNumber] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_URL}/api/social/settings`);
                if (res.ok) setSettings(await res.json());
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const t = lang ? TXT[lang] : TXT.tr;
    const kvkkText = settings?.[`kvkk_text_${lang}`] || '';

    const submit = async () => {
        if (!fullName.trim() || !roomNumber.trim()) {
            toast.error(t.fillAll);
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/social/consent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    full_name: fullName.trim(),
                    room_number: roomNumber.trim(),
                    language: lang,
                    consent: true,
                }),
            });
            if (!res.ok) throw new Error('submit-failed');
            setSubmitted(true);
            setKvkkOpen(false);
            toast.success(t.agreed);
        } catch (e) {
            toast.error('Error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300">
                <Loader2 className="w-10 h-10 text-slate-500 animate-spin" />
            </div>
        );
    }

    // ---------- Language picker ----------
    if (!lang) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300 px-4">
                <Toaster position="top-center" richColors />
                <div className="text-center mb-10">
                    <img src="/grandkaptan-logo.png" alt="Grand Kaptan Hotel" className="max-w-[200px] w-full h-auto mx-auto mb-4" />
                    <p className="text-slate-600 mt-2 flex items-center justify-center gap-2 text-sm">
                        <Globe className="w-4 h-4" /> Select your language
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-xl">
                    {LANGS.map((l) => (
                        <button
                            key={l.code}
                            onClick={() => setLang(l.code)}
                            className="bg-white hover:bg-slate-50 border border-slate-200 rounded-2xl p-5 text-slate-800 transition-all hover:scale-105 shadow-md hover:shadow-lg"
                            data-testid={`lang-${l.code}`}
                        >
                            <div className="text-4xl mb-2">{l.flag}</div>
                            <div className="font-semibold text-base">{l.label}</div>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    // ---------- Main page ----------
    const channels = [
        { key: 'youtube', icon: Youtube, color: 'bg-red-600 hover:bg-red-700', label: 'YouTube', url: settings?.youtube_url },
        { key: 'instagram', icon: Instagram, color: 'bg-gradient-to-br from-pink-500 via-purple-500 to-yellow-500 hover:opacity-90', label: 'Instagram', url: settings?.instagram_url },
        { key: 'tiktok', icon: Music2, color: 'bg-black hover:bg-slate-800', label: 'TikTok', url: settings?.tiktok_url },
        { key: 'facebook', icon: Facebook, color: 'bg-blue-600 hover:bg-blue-700', label: 'Facebook', url: settings?.facebook_url },
    ].filter((c) => c.url && c.url.trim());

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300 py-6 px-4">
            <Toaster position="top-center" richColors />
            <div className="max-w-md mx-auto">
                {/* Language switcher - centered */}
                <div className="flex justify-center mb-5">
                    <div className="flex bg-white/70 backdrop-blur rounded-full p-1 shadow-md border border-slate-200">
                        {LANGS.map((l) => (
                            <button
                                key={l.code}
                                onClick={() => setLang(l.code)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${lang === l.code ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                data-testid={`switch-lang-${l.code}`}
                            >
                                {l.flag} {l.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Header */}
                <div className="text-center mb-6">
                    <img src="/grandkaptan-logo.png" alt="Grand Kaptan Hotel" className="max-w-[160px] w-full h-auto mx-auto mb-3" />
                    <h1 className="text-xl font-bold text-slate-800">{settings?.[`welcome_title_${lang}`] || t.title}</h1>
                    <p className="text-sm text-slate-500 mt-1">{settings?.[`welcome_subtitle_${lang}`] || t.subtitle}</p>
                </div>

                {/* Social channels - compact */}
                <div className="space-y-2 mb-6">
                    {channels.length === 0 && (
                        <div className="text-center text-slate-400 py-4 text-sm">
                            (No social links configured yet)
                        </div>
                    )}
                    {channels.map((c) => {
                        const Icon = c.icon;
                        return (
                            <a
                                key={c.key}
                                href={c.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-3 ${c.color} text-white rounded-xl px-4 py-2.5 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5`}
                                data-testid={`social-${c.key}`}
                            >
                                <Icon className="w-5 h-5 flex-shrink-0" />
                                <div className="flex-1 text-left">
                                    <div className="font-semibold text-sm">{c.label}</div>
                                    <div className="text-[11px] opacity-90">{t.subscribe}</div>
                                </div>
                                <div className="text-base opacity-80">→</div>
                            </a>
                        );
                    })}
                </div>

                {/* KVKK Consent block */}
                <Card className="bg-white shadow-lg border border-slate-200">
                    <CardContent className="p-5 space-y-4">
                        <div className="flex items-center gap-2 text-slate-700">
                            <FileText className="w-5 h-5 text-amber-600" />
                            <h2 className="font-bold text-base">{t.kvkkTitle}</h2>
                        </div>
                        <p className="text-xs text-slate-500">{t.formNote}</p>

                        <div className="space-y-2">
                            <div>
                                <label className="text-xs font-medium text-slate-600 block mb-1">{t.nameLabel}</label>
                                <Input
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    placeholder={t.namePh}
                                    className="h-10"
                                    disabled={submitted}
                                    data-testid="consent-fullname"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-600 block mb-1">{t.roomLabel}</label>
                                <Input
                                    value={roomNumber}
                                    onChange={(e) => setRoomNumber(e.target.value)}
                                    placeholder={t.roomPh}
                                    className="h-10"
                                    disabled={submitted}
                                    data-testid="consent-room"
                                />
                            </div>
                        </div>

                        <Button
                            variant="outline"
                            onClick={() => setKvkkOpen(true)}
                            className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid="open-kvkk-btn"
                        >
                            <FileText className="w-4 h-4 mr-2" />
                            {t.kvkkButton}
                        </Button>

                        {submitted && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2 text-emerald-800" data-testid="consent-success">
                                <Check className="w-5 h-5" />
                                <span className="text-sm font-medium">{t.agreed}</span>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <p className="text-center text-xs text-slate-400 mt-5">
                    © Grand Kaptan Hotel · www.kaptanhotels.com
                </p>
            </div>

            {/* KVKK Dialog */}
            <Dialog open={kvkkOpen} onOpenChange={setKvkkOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-amber-600" />
                            {t.kvkkTitle}
                        </DialogTitle>
                        <DialogDescription className="sr-only">{t.formNote}</DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-auto py-2 pr-2">
                        <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">
                            {kvkkText}
                        </pre>
                    </div>
                    <div className="border-t pt-3 flex flex-col sm:flex-row gap-2">
                        <Button variant="outline" className="flex-1" onClick={() => setKvkkOpen(false)} data-testid="kvkk-close-btn">
                            ✕
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={submitting || submitted}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                            data-testid="kvkk-agree-btn"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                            {t.agreeButton}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
